import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

interface ATTOMStatus {
  configured: boolean;
  enriched_properties: number;
  total_properties: number;
}

function formatCurrency(val?: number | null): string {
  if (!val) return '—';
  return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export default function ATTOMEnrich() {
  const [tab, setTab] = useState<'enrich' | 'view'>('enrich');
  const [status, setStatus] = useState<ATTOMStatus | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState<any>(null);
  const [viewData, setViewData] = useState<any>(null);
  const [viewId, setViewId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Batch enrich
  const [batchIds, setBatchIds] = useState('');
  const [batchResult, setBatchResult] = useState<any>(null);
  const [batchEnriching, setBatchEnriching] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.getATTOMStatus();
      setStatus(res);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus().finally(() => setLoading(false));
  }, [fetchStatus]);

  const enrichProperty = async () => {
    if (!propertyId.trim()) return;
    setEnriching(true);
    setError('');
    setEnrichResult(null);
    try {
      const res = await api.enrichWithATTOM(propertyId);
      setEnrichResult(res);
      await fetchStatus();
    } catch (err: any) {
      setError(err.message || 'Enrichment failed');
    } finally {
      setEnriching(false);
    }
  };

  const enrichBatch = async () => {
    const ids = batchIds.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (ids.length === 0) return;
    setBatchEnriching(true);
    setBatchResult(null);
    setError('');
    try {
      const res = await api.enrichBatchATTOM(ids);
      setBatchResult(res);
      await fetchStatus();
    } catch (err: any) {
      setError(err.message || 'Batch enrichment failed');
    } finally {
      setBatchEnriching(false);
    }
  };

  const viewATTOM = async () => {
    if (!viewId.trim()) return;
    setError('');
    setViewData(null);
    try {
      const res = await api.getATTOMData(viewId);
      setViewData(res);
    } catch (err: any) {
      setError(err.message || 'Failed to load ATTOM data');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">ATTOM Enrichment</h1>
          <p className="text-sm text-gray-500 mt-1">Enrich properties with ATTOM data — owner info, assessed values, building specs, sale history</p>
        </div>

        {/* Status */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <>
            {!status?.configured && (
              <div className="bg-warning-wash border border-warning/30 rounded-lg p-4">
                <p className="text-sm text-warning font-medium">ATTOM Not Configured</p>
                <p className="text-xs text-warning mt-1">Set ATTOM_API_KEY environment variable on Railway.</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <p className={`text-lg font-bold ${status?.configured ? 'text-success' : 'text-red-600'}`}>
                  {status?.configured ? 'Connected' : 'Not Connected'}
                </p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500 uppercase">Enriched Properties</p>
                <p className="text-2xl font-bold text-gray-900">{status?.enriched_properties || 0}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500 uppercase">Total Properties</p>
                <p className="text-2xl font-bold text-gray-900">{status?.total_properties || 0}</p>
              </div>
            </div>
          </>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { key: 'enrich' as const, label: 'Enrich Properties' },
              { key: 'view' as const, label: 'View ATTOM Data' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`pb-3 text-sm font-medium border-b-2 ${
                  tab === t.key
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
        )}

        {tab === 'enrich' ? (
          <div className="space-y-6">
            {/* Single enrich */}
            <div className="bg-white rounded-lg border p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Single Property Enrichment</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  placeholder="Property ID (from Data Hub)"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                />
                <button
                  onClick={enrichProperty}
                  disabled={enriching || !propertyId.trim()}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                >
                  {enriching ? 'Enriching...' : 'Enrich'}
                </button>
              </div>
              {enrichResult && !enrichResult.error && (
                <div className="bg-success-wash border border-success/30 rounded-lg p-3 space-y-2">
                  <p className="text-sm text-success font-medium">Enrichment Successful</p>
                  {enrichResult.owner?.owner1?.fullName && (
                    <p className="text-sm text-success">Owner: {enrichResult.owner.owner1.fullName}</p>
                  )}
                  {enrichResult.assessment?.assessed?.assdTtlValue && (
                    <p className="text-sm text-success">Assessed Value: {formatCurrency(enrichResult.assessment.assessed.assdTtlValue)}</p>
                  )}
                  {enrichResult.sale_history?.length > 0 && (
                    <p className="text-sm text-success">Sale History Records: {enrichResult.sale_history.length}</p>
                  )}
                </div>
              )}
            </div>

            {/* Batch enrich */}
            <div className="bg-white rounded-lg border p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Batch Enrichment (max 25)</h3>
              <textarea
                value={batchIds}
                onChange={(e) => setBatchIds(e.target.value)}
                placeholder="Enter property IDs, one per line or comma-separated"
                rows={4}
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={enrichBatch}
                disabled={batchEnriching || !batchIds.trim()}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                {batchEnriching ? 'Processing...' : 'Enrich Batch'}
              </button>
              {batchResult && (
                <div className="bg-success-wash border border-success/30 rounded-lg p-3 text-sm text-success">
                  <p>Enriched: {batchResult.enriched} | Failed: {batchResult.failed}</p>
                  {batchResult.errors?.length > 0 && (
                    <ul className="mt-1 list-disc list-inside text-red-600">
                      {batchResult.errors.map((e: string, i: number) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">View Cached ATTOM Data</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={viewId}
                  onChange={(e) => setViewId(e.target.value)}
                  placeholder="Property ID"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                />
                <button
                  onClick={viewATTOM}
                  disabled={!viewId.trim()}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                >
                  Load
                </button>
              </div>
            </div>

            {viewData && (
              <div className="bg-white rounded-lg border p-4 space-y-4">
                {!viewData.has_attom_data ? (
                  <p className="text-sm text-gray-500">No ATTOM data cached. Enrich this property first.</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">ATTOM Data</h3>
                      <span className="text-xs text-gray-400">Last fetched: {viewData.last_fetched ? new Date(viewData.last_fetched).toLocaleDateString() : '—'}</span>
                    </div>

                    {/* Owner */}
                    {viewData.attom_data?.owner && (
                      <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase">Owner Info</p>
                        {viewData.attom_data.owner.owner1?.fullName && (
                          <p className="text-sm text-gray-900">Owner: {viewData.attom_data.owner.owner1.fullName}</p>
                        )}
                        {viewData.attom_data.owner.mailingAddressOneLine && (
                          <p className="text-sm text-gray-700">Mailing: {viewData.attom_data.owner.mailingAddressOneLine}</p>
                        )}
                        {viewData.attom_data.owner.corporateInd && (
                          <p className="text-sm text-gray-700">Corporate: {viewData.attom_data.owner.corporateInd}</p>
                        )}
                        {viewData.attom_data.owner.absenteeInd && (
                          <p className="text-sm text-gray-700">Absentee: {viewData.attom_data.owner.absenteeInd}</p>
                        )}
                      </div>
                    )}

                    {/* Assessment */}
                    {viewData.attom_data?.assessment && (
                      <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase">Assessment</p>
                        <div className="grid grid-cols-3 gap-4 mt-2">
                          <div>
                            <p className="text-xs text-gray-400">Land Value</p>
                            <p className="text-sm font-medium">{formatCurrency(viewData.attom_data.assessment.assessed?.assdLandValue)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Improvement Value</p>
                            <p className="text-sm font-medium">{formatCurrency(viewData.attom_data.assessment.assessed?.assdImprValue)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Total Assessed</p>
                            <p className="text-sm font-medium">{formatCurrency(viewData.attom_data.assessment.assessed?.assdTtlValue)}</p>
                          </div>
                        </div>
                        {viewData.attom_data.assessment.tax?.taxAmt && (
                          <p className="text-sm text-gray-700 mt-2">
                            Tax: {formatCurrency(viewData.attom_data.assessment.tax.taxAmt)} ({viewData.attom_data.assessment.tax.taxYear})
                          </p>
                        )}
                      </div>
                    )}

                    {/* Building */}
                    {viewData.attom_data?.building && (
                      <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase">Building Details</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                          {viewData.attom_data.building.size?.bldgSize && (
                            <div>
                              <p className="text-xs text-gray-400">Building Size</p>
                              <p className="text-sm font-medium">{viewData.attom_data.building.size.bldgSize.toLocaleString()} SF</p>
                            </div>
                          )}
                          {viewData.attom_data.building.summary?.yearBuilt && (
                            <div>
                              <p className="text-xs text-gray-400">Year Built</p>
                              <p className="text-sm font-medium">{viewData.attom_data.building.summary.yearBuilt}</p>
                            </div>
                          )}
                          {viewData.attom_data.building.summary?.bldgType && (
                            <div>
                              <p className="text-xs text-gray-400">Building Type</p>
                              <p className="text-sm font-medium">{viewData.attom_data.building.summary.bldgType}</p>
                            </div>
                          )}
                          {viewData.attom_data.building.construction?.constructionType && (
                            <div>
                              <p className="text-xs text-gray-400">Construction</p>
                              <p className="text-sm font-medium">{viewData.attom_data.building.construction.constructionType}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sale History */}
                    {viewData.attom_data?.saleHistory?.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <p className="text-xs font-medium text-gray-500 uppercase">Sale History</p>
                        <div className="space-y-2">
                          {viewData.attom_data.saleHistory.map((sale: any, i: number) => (
                            <div key={i} className="flex items-center justify-between bg-white rounded p-2 text-sm">
                              <div>
                                <span className="font-medium">{formatCurrency(sale.amount?.saleAmt)}</span>
                                <span className="text-gray-400 ml-2">
                                  {sale.date?.saleTransDate || sale.date?.recordingDate || '—'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {sale.transferInfo?.buyerName && `Buyer: ${sale.transferInfo.buyerName}`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
